import { Injectable, inject } from '@angular/core';
import { ExpenditureEntryService } from './expenditure-entry.service';
import { BudgetEntryService } from './budget-entry.service';
import { ProjectModel } from '../../../../../../../core/models/project/project.model';
import {
  ExpenditureRateEntry,
  ProjectExpenditureEntryModel,
} from '../../../../../../../core/models/project/project-expenditure-entry.model';
import { ProjectBudgetEntryModel } from '../../../../../../../core/models/project/project-budget-entry.model';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../../../../core/constants-repository/project-calculation.constants';
import { ProjectDataService } from '../../../../state-and-data-handling/project-data.service';
import {
  ProjectMemberGridRow,
  ProjectTaskGridRow,
} from '../../../../../shared/project-detail-types';

@Injectable({
  providedIn: 'root',
})
export class ProjectExpenditureBudgetTransformerService {
  private readonly expenditureEntryService = inject(ExpenditureEntryService);
  private readonly budgetEntryService = inject(BudgetEntryService);
  private readonly projectDataService = inject(ProjectDataService);

  public transformExpenditureAndBudgetEntriesToMemberGridData(
    gridData: ProjectMemberGridRow[],
    project: ProjectModel,
  ): void {
    project.projectMemberPropertiesWrapper.forEach((projectMemberWrapper, index) => {
      // set expenditure
      if (project.expenditureType === ExpenditureOptions.HourlyRatePerMember) {
        const entriesForMember = project.projectExpenditureEntries.filter(
          (entry) => entry.memberId === projectMemberWrapper.member.id,
        );
        gridData[index].expenditureRateHistory = entriesForMember.map(
          (projectExpenditureEntryModel, index) => {
            return this.expenditureEntryService.createExpenditureEntry(
              projectExpenditureEntryModel.expenditureValue,
              projectExpenditureEntryModel.startingFrom,
              index,
            );
          },
        );
      }

      // set budget
      if (project.budgetType === BudgetOptions.HoursPerMember) {
        const budgetEntry = this.budgetEntryService.getEntryForMember(
          project,
          projectMemberWrapper.member.id,
        );
        gridData[index].budgetSeconds = budgetEntry.seconds;
      }
    });
  }

  public transformMemberGridDataToExpenditureAndBudgetEntries(
    gridData: ProjectMemberGridRow[],
  ): [ProjectExpenditureEntryModel[], ProjectBudgetEntryModel[]] {
    const expenditureEntries: ProjectExpenditureEntryModel[] = [];
    const budgetEntries: ProjectBudgetEntryModel[] = [];

    gridData.forEach((memberGridRow) => {
      const memberId = memberGridRow.member.id;

      memberGridRow.expenditureRateHistory.forEach((expenditureRateEntry) => {
        const entry = this.expenditureEntryService.createExpenditureEntryModel(
          expenditureRateEntry.rate,
          expenditureRateEntry.startingFrom,
          memberId,
          'memberId',
        );
        expenditureEntries.push(entry);
      });

      const budgetEntry = this.budgetEntryService.createBudgetEntry(
        memberGridRow.budgetSeconds,
        'seconds',
        null,
        memberId,
      );
      budgetEntries.push(budgetEntry);
    });
    return [expenditureEntries, budgetEntries];
  }

  public transformExpenditureEntriesExpenditureHourlyValueFixed(
    project: ProjectModel,
  ): ExpenditureRateEntry[] {
    if (project.expenditureType !== ExpenditureOptions.HourlyRateFixed) {
      return [this.expenditureEntryService.createExpenditureEntry(0, null, 0)];
    }
    return project.projectExpenditureEntries.map((projectExpenditureEntry, index) => {
      return this.expenditureEntryService.createExpenditureEntry(
        projectExpenditureEntry.expenditureValue,
        projectExpenditureEntry.startingFrom,
        index,
      );
    });
  }

  public transformHourlyFixedToExpenditureEntries(
    expenditureHistory: ExpenditureRateEntry[],
  ): ProjectExpenditureEntryModel[] {
    return expenditureHistory.map((expenditureEntry) => {
      return this.expenditureEntryService.createExpenditureEntryModel(
        expenditureEntry.rate,
        expenditureEntry.startingFrom,
        null,
        null,
      );
    });
  }

  public transformExpenditureAndBudgetEntriesToTaskGridData(
    gridData: ProjectTaskGridRow[],
    project: ProjectModel,
  ): void {
    this.projectDataService.getTasks(project, null, false).map((task, index) => {
      // set expenditure
      if (project.expenditureType === ExpenditureOptions.HourlyRatePerTask) {
        const entriesForTask = this.expenditureEntryService.getEntriesForTask(project, task.id);
        gridData[index].expenditureRateHistory = entriesForTask.map((entry, entryIndex) => {
          return this.expenditureEntryService.createExpenditureEntry(
            entry.expenditureValue,
            entry.startingFrom,
            entryIndex,
          );
        });
      }

      // set budget
      if (project.budgetType === BudgetOptions.MonetaryPerTask) {
        const budgetEntry = this.budgetEntryService.getEntryForTask(project, task.id);
        gridData[index].budgetMonetary = budgetEntry.monetary;
      } else if (project.budgetType === BudgetOptions.HoursPerTask) {
        const budgetEntry = this.budgetEntryService.getEntryForTask(project, task.id);
        gridData[index].budgetSeconds = budgetEntry.seconds;
      }
    });
  }

  public transformTaskGridDataToExpenditureAndBudgetEntries(
    gridData: ProjectTaskGridRow[],
    budgetPerTask: boolean,
  ): [ProjectExpenditureEntryModel[], ProjectBudgetEntryModel[]] {
    const expenditureEntries: ProjectExpenditureEntryModel[] = [];
    const budgetEntries: ProjectBudgetEntryModel[] = [];

    gridData.forEach((taskGridRow) => {
      taskGridRow.expenditureRateHistory.forEach((expenditureRateEntry) => {
        const entry = this.expenditureEntryService.createExpenditureEntryModel(
          expenditureRateEntry.rate,
          expenditureRateEntry.startingFrom,
          taskGridRow.task.id,
          'taskId',
        );
        expenditureEntries.push(entry);
      });

      const budgetValue = budgetPerTask ? taskGridRow.budgetMonetary : taskGridRow.budgetSeconds;
      const budgetType = budgetPerTask ? 'monetary' : 'seconds';
      const entry = this.budgetEntryService.createBudgetEntry(
        budgetValue,
        budgetType,
        taskGridRow.task.id,
        null,
      );
      budgetEntries.push(entry);
    });
    return [expenditureEntries, budgetEntries];
  }
}
